import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { panelsApi } from '../api';
import { FiArrowLeft, FiPlus, FiTrash2, FiEdit2, FiUser, FiMail, FiShield, FiCheck, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function AdminAccountsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', email: '', role: 'admin' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [panelRes, adminsRes] = await Promise.all([
        panelsApi.getById(id),
        panelsApi.getAdminAccounts(id)
      ]);
      setPanel(panelRes.data.panel);
      setAdmins(adminsRes.data.admins || []);
    } catch (err) {
      toast.error('Failed to load data');
      navigate('/panels');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditing(null); setForm({ username: '', password: '', email: '', role: 'admin' }); setShowForm(true); };

  const openEdit = (admin) => {
    setEditing(admin);
    setForm({ username: admin.username, password: '', email: admin.email || '', role: admin.role || 'admin' });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing && !form.password) { toast.error('Password required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing) {
        await panelsApi.updateAdminAccount(id, editing.id, payload);
        toast.success('Admin updated + GoDesk synced');
      } else {
        await panelsApi.createAdminAccount(id, payload);
        toast.success('Admin created + GoDesk synced');
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (admin) => {
    try {
      await panelsApi.updateAdminAccount(id, admin.id, { isActive: admin.is_active ? false : true });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async (admin) => {
    if (!confirm(`Delete admin "${admin.username}"?`)) return;
    try {
      await panelsApi.deleteAdminAccount(id, admin.id);
      toast.success('Admin deleted + GoDesk synced');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <button onClick={() => navigate(`/panels/${id}`)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm font-medium transition-colors mb-6">
        <FiArrowLeft size={14} /> Back to {panel?.panel_name || panel?.domain}
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Admin Accounts</h1>
          <p className="text-muted-foreground text-sm mt-1">{panel?.domain} — {admins.length} accounts</p>
        </div>
        <button onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors">
          <FiPlus size={16} /> Add Admin
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto min-h-[200px]">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Username</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Email</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Role</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Status</th>
                <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {admins.map(admin => (
                <tr key={admin.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="p-4 align-middle font-medium text-foreground">
                    <FiUser size={13} className="inline mr-1.5 text-muted-foreground" />{admin.username}
                  </td>
                  <td className="p-4 align-middle text-muted-foreground text-xs">{admin.email || '—'}</td>
                  <td className="p-4 align-middle">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      admin.role === 'super_admin' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400'
                    }`}>
                      <FiShield size={11} className="mr-1" />{admin.role?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-4 align-middle">
                    <button onClick={() => handleToggle(admin)}
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border cursor-pointer transition-colors ${
                        admin.is_active
                          ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                          : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                      }`}>
                      {admin.is_active ? <FiCheck size={11} /> : <FiX size={11} />}
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="p-4 align-middle text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(admin)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors" title="Edit">
                        <FiEdit2 size={14} />
                      </button>
                      <button onClick={() => handleDelete(admin)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title="Delete">
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {admins.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    No admin accounts yet. Click "Add Admin" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                {editing ? 'Edit Admin Account' : 'Add Admin Account'}
              </h3>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Username</label>
                <input type="text" required value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                  className={inputClass} placeholder="admin" disabled={!!editing} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">
                  Password {editing && <span className="text-muted-foreground font-normal">(leave blank to keep current)</span>}
                </label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  className={inputClass} placeholder={editing ? 'Leave blank to keep' : 'Min 6 characters'} minLength={editing ? 0 : 6} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className={inputClass} placeholder="admin@example.com" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className={inputClass}>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="support">Support</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-secondary h-10 px-4 py-2">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
